import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const scrollToAbout = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (location.pathname === "/") {
      document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      // Wait for navigation then scroll
      setTimeout(() => {
        document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/#about", label: "Over ons", onClick: scrollToAbout },
    { href: "/offerte", label: "Offerte" },
  ];

  return (
    <>
      <header
        data-testid="header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? "bg-cream/95 backdrop-blur-md border-b border-burgundy/5" 
            : "bg-cream/80 backdrop-blur-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo - Clean, modern font */}
            <Link
              to="/"
              data-testid="logo-link"
              className="flex flex-col transition-colors duration-300 hover:text-blue group"
            >
              <span className="text-burgundy text-xl md:text-2xl tracking-[0.15em] uppercase font-light group-hover:text-blue transition-colors duration-300">
                Duet Dining
              </span>
              <span className="text-burgundy/50 text-[10px] tracking-[0.2em] uppercase group-hover:text-blue/50 transition-colors duration-300">
                Music. Food. Vibes.
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={link.onClick}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  className="text-sm tracking-wide uppercase text-burgundy transition-colors duration-300 hover:text-blue"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/offerte"
                data-testid="nav-cta-button"
                className="text-sm tracking-widest uppercase px-6 py-3 border border-burgundy text-burgundy hover:bg-burgundy hover:text-white transition-all duration-300"
              >
                Contact
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-button"
              className="md:hidden p-2 text-burgundy transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-cream pt-24 px-6"
          >
            <nav className="flex flex-col gap-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.href}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                    className="text-2xl font-light text-burgundy tracking-wide"
                    onClick={link.onClick ?? (() => setIsMobileMenuOpen(false))}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="pt-8"
              >
                <Link
                  to="/offerte"
                  data-testid="mobile-nav-cta-button"
                  className="btn-primary inline-block"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Vraag offerte aan
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
